import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";

import { useConsumerAuth } from "../context/ConsumerAuthContext";
import { readPrivacyPreferences, writePrivacyPreferences } from "../lib/privacyPreferences";

export default function PrivacyControls() {
  const consumer = useConsumerAuth();
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState(readPrivacyPreferences);

  useEffect(() => {
    const accountPrivacy = consumer?.account?.privacy;
    if (!accountPrivacy) return;
    setPreferences((current) => ({
      ...current,
      service_analytics: accountPrivacy.service_analytics === true,
      advertising_personalisation: accountPrivacy.advertising_personalisation === true,
    }));
  }, [consumer?.account?.privacy]);

  const persist = async (next) => {
    if (consumer?.enabled && consumer?.account) {
      await consumer.mutate("patch", "/me/privacy", next);
      await consumer.refresh();
    }
    const saved = writePrivacyPreferences(next);
    setPreferences(saved);
    setOpen(false);
  };

  const save = async () => {
    try {
      await persist(preferences);
      toast.success("Privacy choices saved.");
    } catch (_error) {
      toast.error("Privacy choices could not be saved.");
    }
  };

  const rejectOptional = async () => {
    try {
      await persist({ service_analytics: false, advertising_personalisation: false });
      toast.success("Optional storage remains off.");
    } catch (_error) {
      toast.error("Privacy choices could not be saved.");
    }
  };

  return (
    <>
      <button
        type="button"
        className="text-white/70 hover:text-[var(--cc-gold)]"
        onClick={() => setOpen(true)}
        data-testid="privacy-choices-button"
      >
        Privacy choices
      </button>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/65 p-3 sm:items-center sm:p-6">
          <section
            aria-labelledby="privacy-choices-title"
            aria-modal="true"
            className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-md border border-white/15 bg-[var(--cc-navy-2)] p-5 shadow-2xl sm:p-7"
            role="dialog"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="overline text-[var(--cc-gold)]">Privacy</p>
                <h2 id="privacy-choices-title" className="mt-1 font-display text-2xl font-semibold">Privacy choices</h2>
              </div>
              <button type="button" aria-label="Close privacy choices" onClick={() => setOpen(false)}>
                <X size={22} />
              </button>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-white/55">
              Necessary storage keeps requested features secure. Optional analytics and advertising storage are off unless you choose them.
            </p>
            <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
              <PreferenceRow checked disabled label="Strictly necessary" description="Authentication, security and saving these choices. Always on." />
              <PreferenceRow
                checked={preferences.service_analytics}
                label="Service analytics"
                description="Allow aggregate measurement used to improve Competition Checker."
                onChange={(value) => setPreferences((current) => ({ ...current, service_analytics: value }))}
              />
              <PreferenceRow
                checked={preferences.advertising_personalisation}
                label="Advertising and personalisation"
                description="Allow optional advertising or personalisation storage when those services are configured."
                onChange={(value) => setPreferences((current) => ({ ...current, advertising_personalisation: value }))}
              />
            </div>
            <p className="mt-4 text-xs leading-relaxed text-white/45">
              Marketing email and anonymous insight-sharing choices are separate account settings. No external insight sharing is enabled in this foundation.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button type="button" className="rounded border border-white/20 px-4 py-2 text-sm" onClick={rejectOptional}>Reject optional</button>
              <button type="button" className="btn-gold rounded px-4 py-2 text-sm" onClick={save}>Save choices</button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function PreferenceRow({ checked, description, disabled = false, label, onChange }) {
  return (
    <label className="flex items-center justify-between gap-5 py-4">
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="mt-1 block text-xs leading-relaxed text-white/45">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        className="h-5 w-5 shrink-0 accent-[var(--cc-gold)]"
        onChange={onChange ? (event) => onChange(event.target.checked) : undefined}
      />
    </label>
  );
}
